import type { AssignableRole } from '@ralph/contracts'
import clsx from 'clsx'
import { Clock, TriangleAlert, UserPlus } from 'lucide-react'
import { useId, useState } from 'react'
import { signInRefusalCause } from '@/components/people/people-plane.ts'
import { Notice, riseDelay, SectionHead, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, EmptyState, Input, Select } from '@/components/ui/primitives.tsx'
import {
  useAccessSettings,
  usePendingGrants,
  usePrepareGrant,
  useSaveAccessSettings,
  useSignInFailures,
  useWithdrawPendingGrant
} from '@/hooks/use-people.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'

/**
 * The three surfaces that are about access rather than about one person.
 *
 * They are one tab rather than three screens because an administrator arriving here has one of
 * three closely related questions — somebody starts on Monday, somebody cannot get in, or the
 * rules of the plane itself need changing — and splitting those across a navigation tree would
 * make two of them things nobody finds.
 *
 * There is no "a whole group gets a role" surface any more: groups decide what people can *use*,
 * never what they may *do* (PRD-26 D-24), so a role is always given to one person, here or on
 * their profile.
 */

/** Least privilege first: the grant form opens on the role that opens the least. */
const ASSIGNABLE: AssignableRole[] = ['auditor', 'operator', 'admin']

type Tab = 'pending' | 'failures' | 'settings'

export const AccessPanel = ({ canWrite, onOpenPerson }: { canWrite: boolean; onOpenPerson: (id: string) => void }) => {
  const t = useI18n().t.people
  const [tab, setTab] = useState<Tab>('pending')
  const tabs: Tab[] = canWrite ? ['pending', 'failures', 'settings'] : ['pending', 'failures']
  // Read inside the body, never at module scope: a map built at import renders the previous
  // language after a locale switch, with nothing anywhere reporting it (PRD-27 F-3).
  const tabLabel: Record<Tab, string> = {
    pending: t.access.pendingTab,
    failures: t.access.failuresTab,
    settings: t.access.settingsTab
  }

  return (
    <div className="fade-in px-5 py-5 sm:px-8">
      <div role="tablist" aria-label={t.access.title} className="flex flex-wrap gap-1.5">
        {tabs.map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            id={`access-tab-${value}`}
            aria-selected={tab === value}
            aria-controls={`access-panel-${value}`}
            onClick={() => setTab(value)}
            className={clsx(
              'h-[30px] shrink-0 rounded-full border px-3.5 text-[12.5px] tracking-[-0.01em]',
              'transition-[color,background-color,border-color,transform] duration-200 ease-out',
              tab === value
                ? 'border-rule-strong bg-card-solid font-semibold text-fg'
                : 'border-transparent text-fg-muted hover:-translate-y-px hover:border-rule-strong hover:text-fg'
            )}
          >
            {tabLabel[value]}
          </button>
        ))}
      </div>

      <div
        key={tab}
        role="tabpanel"
        id={`access-panel-${tab}`}
        aria-labelledby={`access-tab-${tab}`}
        className="fade-in mt-5"
      >
        {tab === 'pending' && <PendingGrants canWrite={canWrite} />}
        {tab === 'failures' && <SignInFailures onOpenPerson={onOpenPerson} />}
        {tab === 'settings' && canWrite && <AccessSettingsForm />}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

const PendingGrants = ({ canWrite }: { canWrite: boolean }) => {
  const fmt = useI18n()
  const t = fmt.t.people
  const pending = usePendingGrants(true)
  const prepare = usePrepareGrant()
  const withdraw = useWithdrawPendingGrant()

  const emailId = useId()
  const roleId = useId()
  const reasonId = useId()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<AssignableRole>('auditor')
  const [reason, setReason] = useState('')

  const items = pending.data?.items ?? []

  return (
    <section>
      <SectionHead
        title={t.access.pending.title}
        lead={t.access.pending.lead}
        action={
          canWrite ? (
            <Button size="sm" icon={<UserPlus size={13} aria-hidden />} onClick={() => setOpen((value) => !value)}>
              {t.access.pending.add}
            </Button>
          ) : undefined
        }
      />

      {open && canWrite && (
        <StudioRow className="mt-3">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
            <div>
              <label htmlFor={emailId} className="eyebrow">
                {t.access.pending.emailLabel}
              </label>
              <div className="mt-1.5">
                <Input
                  id={emailId}
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t.access.pending.emailPlaceholder}
                />
              </div>
            </div>
            <div>
              <label htmlFor={roleId} className="eyebrow">
                {t.access.pending.roleLabel}
              </label>
              <div className="mt-1.5">
                <Select id={roleId} value={role} onChange={(event) => setRole(event.target.value as AssignableRole)}>
                  {ASSIGNABLE.map((value) => (
                    <option key={value} value={value}>
                      {t.roleLabel[value]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          <div className="mt-3">
            <label htmlFor={reasonId} className="eyebrow">
              {t.access.pending.reasonLabel}
            </label>
            <div className="mt-1.5">
              <Input id={reasonId} value={reason} onChange={(event) => setReason(event.target.value)} />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={prepare.isPending || email.trim() === ''}
              onClick={() =>
                prepare.mutate(
                  { email: email.trim(), role, reason: reason.trim() },
                  {
                    onSuccess: () => {
                      setEmail('')
                      setReason('')
                    }
                  }
                )
              }
            >
              {t.access.pending.submit}
            </Button>
            {prepare.isSuccess && (
              <p role="status" className="text-[12.5px] text-fg-muted">
                {prepare.data.applied
                  ? prepare.data.pendingApproval
                    ? t.access.pending.appliedWaiting(email)
                    : t.access.pending.applied(email)
                  : prepare.data.pendingApproval
                    ? t.access.pending.preparedWaiting(email)
                    : t.access.pending.prepared(email)}
              </p>
            )}
            {prepare.isError && (
              <p role="alert" className="text-[12.5px] text-alert">
                {describeApiError(fmt.t, prepare.error)}
              </p>
            )}
          </div>
        </StudioRow>
      )}

      {items.length === 0 ? (
        <div className="mt-4">
          <EmptyState title={t.access.pending.empty} body={t.access.pending.emptyBody} />
        </div>
      ) : (
        <ul className="mt-4 space-y-2.5">
          {items.map((item, index) => (
            <li key={item.id}>
              <StudioRow style={riseDelay(index)}>
                <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                  <div className="min-w-0 flex-1 basis-[15rem]">
                    <p className="text-[13.5px] font-semibold">{item.email}</p>
                    <p className="readout mt-1 text-fg-subtle">
                      {t.roleLabel[item.role]} · {item.grantedByName ?? '—'} · {fmt.formatAgo(item.grantedAt)}
                      {item.claimExpiresAt && ` · ${t.access.pending.claimBy(fmt.formatUntil(item.claimExpiresAt))}`}
                    </p>
                    {item.reason !== '' && (
                      <p className="mt-1 text-[12.5px] italic text-fg-muted">{t.provenance.reason(item.reason)}</p>
                    )}
                    {item.pendingApproval && (
                      <div className="mt-2">
                        <Notice title={t.access.pending.needsApproval} icon={<TriangleAlert size={12} aria-hidden />}>
                          {t.access.pending.needsApprovalHint}
                        </Notice>
                      </div>
                    )}
                    {!item.claimable && (
                      <div className="mt-2">
                        <Notice title={t.access.pending.unclaimable} icon={<TriangleAlert size={12} aria-hidden />} />
                      </div>
                    )}
                  </div>
                  {canWrite && (
                    <Button size="sm" variant="ghost" onClick={() => withdraw.mutate(item.id)}>
                      {t.access.pending.withdraw}
                    </Button>
                  )}
                </div>
              </StudioRow>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------

const SignInFailures = ({ onOpenPerson }: { onOpenPerson: (id: string) => void }) => {
  const fmt = useI18n()
  const t = fmt.t.people
  const failures = useSignInFailures(true)
  const items = failures.data?.items ?? []

  return (
    <section>
      <SectionHead title={t.access.failures.title} lead={t.access.failures.lead} />

      {items.length === 0 ? (
        <div className="mt-4">
          <EmptyState title={t.access.failures.empty} body={t.access.failures.emptyBody} />
        </div>
      ) : (
        <ul className="mt-4 space-y-2.5">
          {items.map((failure, index) => (
            // Keyed on the structural cause, never on the prose. A React key built from a
            // server sentence changes when that sentence is translated, which remounts every
            // row on a language switch — and it is the same "display text as identity" shape
            // that made the command palette lose its icons (PRD-27 F-9).
            <li key={`${failure.email}-${failure.messageKey ?? 'unknown'}`}>
              <StudioRow style={riseDelay(index)}>
                <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                  <div className="min-w-0 flex-1 basis-[15rem]">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[13.5px] font-semibold">{failure.email}</p>
                      <Badge tone="neutral">{t.access.failures.attempts(fmt, failure.attempts)}</Badge>
                    </div>
                    {/* Remediation, not error text: the cause decides the sentence, the
                        server's own prose is never shown. */}
                    <p className="mt-1 text-[12.5px] text-fg-muted">
                      {t.access.failures.reason[signInRefusalCause(failure)]}
                    </p>
                    <p className="readout mt-1 inline-flex items-center gap-1 text-fg-subtle">
                      <Clock size={10} aria-hidden />
                      {fmt.formatAgo(failure.lastAt)} · {failure.provider}
                    </p>
                  </div>
                  {failure.userId && (
                    <Button size="sm" variant="ghost" onClick={() => onOpenPerson(failure.userId as string)}>
                      {t.access.failures.reactivate}
                    </Button>
                  )}
                </div>
              </StudioRow>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------

const AccessSettingsForm = () => {
  const fmt = useI18n()
  const t = fmt.t.people
  const settings = useAccessSettings(true)
  const save = useSaveAccessSettings()
  const fourEyesId = useId()
  const ttlId = useId()

  const current = settings.data
  if (!current) return <p className="text-[13px] text-fg-subtle">{t.list.loading}</p>

  return (
    <section>
      <SectionHead title={t.access.settings.title} lead={t.access.settings.lead} />

      <StudioRow className="mt-4 space-y-4">
        <div>
          <label htmlFor={fourEyesId} className="flex items-start gap-2.5 text-[13.5px]">
            <input
              id={fourEyesId}
              type="checkbox"
              className="mt-[3px]"
              checked={current.secondAdminForAdminGrants}
              onChange={(event) => save.mutate({ ...current, secondAdminForAdminGrants: event.target.checked })}
            />
            <span>
              {t.access.settings.fourEyes}
              <span className="mt-0.5 block text-[12.5px] leading-[1.5] text-fg-muted">
                {t.access.settings.fourEyesHint}
              </span>
            </span>
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor={ttlId} className="eyebrow">
              {t.access.settings.ttl}
            </label>
            <div className="mt-1.5 w-[6rem]">
              <Input
                id={ttlId}
                type="number"
                min={1}
                max={365}
                value={current.pendingGrantTtlDays}
                onChange={(event) => save.mutate({ ...current, pendingGrantTtlDays: Number(event.target.value) || 30 })}
              />
            </div>
          </div>
          <span className="pb-2 text-[13px] text-fg-muted">{t.access.settings.ttlUnit}</span>
        </div>

        {save.isSuccess && (
          <p role="status" className="text-[12.5px] text-fg-muted">
            {t.access.settings.saved}
          </p>
        )}
        {save.isError && (
          <p role="alert" className="text-[12.5px] text-alert">
            {describeApiError(fmt.t, save.error)}
          </p>
        )}
      </StudioRow>
    </section>
  )
}
